import "./splashScreen.scss";
import { useEffect } from "react";
import { logInfo } from "@/Utils/logger";
import { Spinner } from "@/Components/Spinner";

const isNeemsEnv = process.env.REACT_APP_IS_NEEMS_ENV === "true";
const isStyleMatchEnv = process.env.REACT_APP_IS_STYLEMATCH_ENV === "true";
const isSisleyEnv = process.env.REACT_APP_IS_SISLEY_ENV === "true";

const splashLogoSrc = isStyleMatchEnv
  ? "/images/stylematch-logo.svg"
  : isNeemsEnv
  ? "/images/neems-splash-screen.svg"
  : isSisleyEnv
  ? "/images/sisley-splash-screen.svg"
  : "/images/logo.png";

type SplashScreenProps = {
  showSpinner: boolean;
  // Auth produced no user within App.tsx's splash timeout. The app cannot do
  // anything without a Firebase user (every query/write is keyed on auth.uid),
  // so show an honest "can't connect" + Retry instead of a spinner that never
  // resolves. Retry reloads, restarting the full auth flow.
  showRetry?: boolean;
};

const SplashScreen: React.FC<SplashScreenProps> = ({
  showSpinner,
  showRetry = false,
}) => {
  useEffect(() => {
    // calc the time it takes for the user to load (Splash screen to go away)
    const startTime = performance.now();
    return () => {
      const endTime = performance.now();
      const timeTaken = endTime - startTime;
      logInfo("SplashScreen", { timeTaken });
    };
  }, []);

  return (
    <div className="splash-screen">
      {/* The retry state is a taller, self-contained message rather than the
          logo-plus-short-hint the base layout is tuned for, so it gets its own
          centering — see splashScreen.scss. */}
      <div
        className={
          showRetry
            ? "splash-screen-content splash-screen-content--retry"
            : "splash-screen-content"
        }
      >
        <img
          src={splashLogoSrc}
          alt="Loading..."
          // The Sisley wordmark asset is a wide, high-res PNG; the base splash
          // img uses the asset's natural size (default logo.png is 185px). Cap
          // the Sisley display width so it renders at a sensible size. Scoped to
          // Sisley — no effect on any other tenant's splash.
          style={isSisleyEnv ? { width: "220px", height: "auto" } : undefined}
        />
        <div className="spinner-container">
          {showRetry ? (
            <div className="inner-container splash-retry">
              <span>
                Unable to connect. Please check your internet connection and
                try again.
              </span>
              <button
                type="button"
                className="splash-retry__button"
                onClick={() => window.location.reload()}
              >
                Retry
              </button>
            </div>
          ) : (
            showSpinner && (
              <div className="inner-container">
                <span>Loading...</span>
                <Spinner />
              </div>
            )
          )}
        </div>
      </div>
      {isStyleMatchEnv && (
        <p className="splash-attribution">
          Body scanning powered by TrueToForm
        </p>
      )}
    </div>
  );
};

export default SplashScreen;
